import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Snackbar, Select, FormControl, InputLabel, Tabs, Tab, TablePagination,
  Avatar
} from '@mui/material';
import {
  Payments as WithdrawIcon,
  Add as AddIcon,
  AccountBalanceWallet as BalanceIcon,
  TrendingUp as EarningsIcon,
  LocalAtm as WithdrawnIcon
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import { useTutorAuth } from '../../context/tutorAuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Earnings() {
  const { tutor } = useTutorAuth();
  const tutorId = tutor?.id;

  const [earningsData, setEarningsData] = useState(null);
  const [bankDetails, setBankDetails] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAddAccount, setOpenAddAccount] = useState(false);
  const [newBank, setNewBank] = useState({ bank_name: '', account_number: '', account_name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [tabIndex, setTabIndex] = useState(0);
  const [paymentSourceTab, setPaymentSourceTab] = useState(0);
  const [paymentPage, setPaymentPage] = useState(0);
  const [withdrawalPage, setWithdrawalPage] = useState(0);
  const rowsPerPage = 10;

  const fetchEarnings = useCallback(async () => {
    if (!tutorId) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/tutors/tutor-earnings/${tutorId}`);
      setEarningsData(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load earnings.');
    } finally {
      setLoading(false);
    }
  }, [tutorId]);

  const fetchBankDetails = useCallback(async () => {
    if (!tutorId) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/tutors/bank-details/${tutorId}`);
      setBankDetails(res.data.data ? [res.data.data] : []);
    } catch {
      setBankDetails([]);
    }
  }, [tutorId]);

  const fetchWithdrawals = useCallback(async () => {
    if (!tutorId) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/tutors/withdrawals/${tutorId}`);
      setWithdrawals(res.data.data || []);
    } catch {
      setWithdrawals([]);
    }
  }, [tutorId]);

  const handleAddAccount = async () => {
    try {
      await axios.post(`${BASE_URL}/api/tutors/bank-details/${tutorId}`, newBank);
      setSnackbar({ open: true, message: 'Bank account added successfully', severity: 'success' });
      setOpenAddAccount(false);
      fetchBankDetails();
    } catch {
      setSnackbar({ open: true, message: 'Failed to add bank account', severity: 'error' });
    }
  };

  const handleWithdraw = async () => {
    if (!selectedBank || !withdrawAmount) {
      return setSnackbar({ open: true, message: 'Please select a bank and enter an amount', severity: 'warning' });
    }
    try {
      await axios.post(`${BASE_URL}/api/tutors/withdraw/${tutorId}`, {
        bank_id: selectedBank,
        amount: withdrawAmount
      });
      setSnackbar({ open: true, message: 'Withdrawal request submitted', severity: 'success' });
      setWithdrawAmount('');
      fetchEarnings();
      fetchWithdrawals();
    } catch (err) {
      const msg = err.response?.data?.message || 'Withdrawal failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  useEffect(() => {
    fetchEarnings();
    fetchBankDetails();
    fetchWithdrawals();
  }, [fetchEarnings, fetchBankDetails, fetchWithdrawals]);

  const formatCurrency = (value) =>
    `₦${parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const handleTabChange = (event, newValue) => setTabIndex(newValue);
  const handlePaymentSourceTabChange = (event, newValue) => {
    setPaymentSourceTab(newValue);
    setPaymentPage(0);
  };
  const handlePaymentPageChange = (event, newPage) => setPaymentPage(newPage);
  const handleWithdrawalPageChange = (event, newPage) => setWithdrawalPage(newPage);

  const visiblePayments = paymentSourceTab === 0
    ? (earningsData?.coursePayments || [])
    : (earningsData?.groupClassPayments || []);

  return (
    <Box sx={{ py: 3, px: { xs: 1, md: 3 }, width: '100%', overflowX: 'hidden' }}>
      <Typography variant="h4" fontWeight="900" sx={{ mb: 4, color: '#1A2027', letterSpacing: '-0.5px' }}>
        Earnings Overview
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Stats Section */}
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 6 } }}>
            {[
              { label: 'Total Earnings', value: earningsData.totalEarnings, icon: <EarningsIcon />, color: '#4caf50', gradient: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)' },
              { label: 'Available Balance', value: earningsData.availableBalance, icon: <BalanceIcon />, color: '#1976d2', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)' },
              { label: 'Total Withdrawn', value: earningsData.totalWithdrawn, icon: <WithdrawnIcon />, color: '#f57c00', gradient: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)' },
              { label: 'Payments Received', value: earningsData.numberOfPayments, icon: <WithdrawIcon />, color: '#9c27b0', gradient: 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)', isRaw: true }
            ].map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card sx={{ 
                  borderRadius: '24px', 
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.04)',
                  background: stat.gradient,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-5px)' }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'white', color: stat.color, boxShadow: '0 4px 10px rgba(0,0,0,0.08)', width: 44, height: 44 }}>
                        {stat.icon}
                      </Avatar>
                    </Box>
                    <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '1px', mb: 0.5 }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" fontWeight="900" sx={{ color: '#1A2027' }}>
                      {stat.isRaw ? stat.value : formatCurrency(stat.value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Withdraw Card */}
          <Card sx={{ mb: 4, borderRadius: '20px', px: { xs: 1, md: 0 } }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Withdraw Funds
              </Typography>

              {bankDetails.length > 0 ? (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="bank-select-label">Select Bank</InputLabel>
                  <Select
                    labelId="bank-select-label"
                    value={selectedBank}
                    label="Select Bank"
                    onChange={(e) => setSelectedBank(e.target.value)}
                  >
                    {bankDetails.map((bank) => (
                      <MenuItem key={bank.bank_id} value={bank.bank_id}>
                        {bank.bank_name} - {bank.account_number} ({bank.account_name})
                      </MenuItem>
                    ))}
                  </Select>

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2, borderRadius: '10px' }}
                    onClick={() => setOpenAddAccount(true)}
                  >
                    Add New Account
                  </Button>
                </FormControl>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddAccount(true)}
                  sx={{ mb: 2, borderRadius: '10px' }}
                >
                  Add Account for Withdrawal
                </Button>
              )}

              <TextField
                label="Amount"
                type="number"
                fullWidth
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
              />

              <Button variant="contained" color="primary" onClick={handleWithdraw} sx={{ borderRadius: '10px', py: 1, px: 4 }}>
                Request Withdrawal
              </Button>
            </CardContent>
          </Card>

          {/* History Section */}
          <Box sx={{ width: '100%', mb: 4 }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="earnings tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              centered
              sx={{ mb: 2 }}
            >
              <Tab label="Payment History" sx={{ fontWeight: 'bold' }} />
              <Tab label="Withdrawal History" sx={{ fontWeight: 'bold' }} />
            </Tabs>

            {tabIndex === 0 && (
              <Card sx={{ borderRadius: '20px' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Payment History
                  </Typography>
                  <Tabs
                    value={paymentSourceTab}
                    onChange={handlePaymentSourceTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ mb: 2 }}
                  >
                    <Tab label="Course Payments" />
                    <Tab label="Group Class Payments" />
                  </Tabs>
                  <TableContainer component={Paper} elevation={0} sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Paid Amount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Earning</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Course Total</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Payment Plan</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Student Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visiblePayments
                          .slice(paymentPage * rowsPerPage, paymentPage * rowsPerPage + rowsPerPage)
                          .map((payment, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{moment(payment.paid_at).format('MMM D, YYYY')}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>
                                {String(payment.payment_for || 'course').replaceAll('_', ' ')}
                              </TableCell>
                              <TableCell>{payment.group_class_title || '-'}</TableCell>
                              <TableCell>{formatCurrency(payment.paid_amount)}</TableCell>
                              <TableCell sx={{ color: 'green', fontWeight: '600' }}>{formatCurrency(payment.earning)}</TableCell>
                              <TableCell>{formatCurrency(payment.course_total)}</TableCell>
                              <TableCell>{payment.payment_plan}</TableCell>
                              <TableCell>{payment.student_email}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={visiblePayments.length}
                    rowsPerPage={rowsPerPage}
                    page={paymentPage}
                    onPageChange={handlePaymentPageChange}
                  />
                </CardContent>
              </Card>
            )}

            {tabIndex === 1 && (
              <Card sx={{ borderRadius: '20px' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Withdrawal History
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 700 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Bank</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {withdrawals
                          .slice(withdrawalPage * rowsPerPage, withdrawalPage * rowsPerPage + rowsPerPage)
                          .map((w) => (
                            <TableRow key={w.withdrawal_id} hover>
                              <TableCell>{moment(w.requested_at).format('MMM D, YYYY')}</TableCell>
                              <TableCell fontWeight="600">{formatCurrency(w.amount)}</TableCell>
                              <TableCell>{w.bank_name} - {w.account_number}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize', color: w.status === 'completed' ? 'green' : 'orange' }}>{w.status}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={withdrawals.length}
                    rowsPerPage={rowsPerPage}
                    page={withdrawalPage}
                    onPageChange={handleWithdrawalPageChange}
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        </>
      )}

      {/* Add Account Modal */}
      <Dialog
        open={openAddAccount}
        onClose={() => setOpenAddAccount(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Add Bank Account</DialogTitle>
        <DialogContent>
          <TextField
            label="Bank Name"
            fullWidth
            sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            value={newBank.bank_name}
            onChange={(e) => setNewBank({ ...newBank, bank_name: e.target.value })}
          />
          <TextField
            label="Account Number"
            fullWidth
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            value={newBank.account_number}
            onChange={(e) => setNewBank({ ...newBank, account_number: e.target.value })}
          />
          <TextField
            label="Account Name"
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            value={newBank.account_name}
            onChange={(e) => setNewBank({ ...newBank, account_name: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenAddAccount(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAccount} sx={{ borderRadius: '10px' }}>
            Save Account
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}